import { Component } from '@angular/core';
import { BlockLifeChanges, LifeChangeItem, LifeChangeStatus } from '@src/app/@shared/models/step.model';
import { BlockAbstractComponent } from '@app/components/blocks/block.abstract.component';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { ButtonVariant } from '@app/components/button/button.component';
import { ApiUrlProgramData, ProgramDataCode } from '@app/courseContent/courseContent.service';
import { getDateTimeNowAsString } from '@app/utils/date';

export enum FormControlName {
  lifeChanges = 'lifeChanges',
}

interface LifeChangesForm {
  [FormControlName.lifeChanges]: FormArray<FormControl<LifeChangeItem>>;
}

@Component({
  selector: 'pl-block-life-changes',
  templateUrl: './block-lifeChanges.component.html',
})
export class BlockLifeChangesComponent extends BlockAbstractComponent<BlockLifeChanges, LifeChangesForm> {
  public readonly FormControlName: typeof FormControlName = FormControlName;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;

  public showAddLifeChangeButton: boolean = true;

  protected override readonly DEBOUNCE_TIME_FORM_CHANGES: number = 500;

  protected override initForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<LifeChangesForm>({
      lifeChanges: fb.nonNullable.array<LifeChangeItem>([]),
    });
  }

  public get formArrayLifeChanges(): FormArray {
    if (this.form) {
      return this.form.get(FormControlName.lifeChanges) as FormArray;
    }

    throw new Error(`formArrayLifeChanges() - form not initialised.`);
  }

  protected override patchFormWithEmptyValue(): void {}

  protected override patchFormFromBlock(block: BlockLifeChanges): void {
    const lifeChangesData: LifeChangeItem[] = block.data.value ? JSON.parse(block.data.value) : [];

    this.formArrayLifeChanges.clear({ emitEvent: false });

    lifeChangesData.forEach((item: LifeChangeItem) => {
      this.formArrayLifeChanges.push(new FormControl(item), { emitEvent: false });
    });
  }

  public deleteLifeChange(indexBlockToDelete: number): void {
    this.formArrayLifeChanges.removeAt(indexBlockToDelete);
  }

  public addLifeChange(): void {
    this.showAddLifeChangeButton = false;
    const newLifeItem: LifeChangeItem = {
      id: Date.now(),
      createdAt: getDateTimeNowAsString(),
      updatedAt: '',
      status: LifeChangeStatus.notStarted,
      title: '',
    };
    this.formArrayLifeChanges.push(new FormControl(newLifeItem), { emitEvent: false });
  }

  protected override getUpdatedBlock(): BlockLifeChanges {
    if (this.form && this.block) {
      const lifeChangeItems: LifeChangeItem[] = this.formArrayLifeChanges.value;
      this.showAddLifeChangeButton = this.shouldDisplayAddNewLifeChangeButton(lifeChangeItems);

      const updatedBlock: BlockLifeChanges = {
        ...this.block,
        data: {
          value: JSON.stringify(lifeChangeItems),
        },
      };

      this.updateBackEndWithUpdatedLifeChanges(updatedBlock);

      return updatedBlock;
    }

    throw new Error(`getUpdatedBlock() this.form or control should not be null when this function is called`);
  }

  private shouldDisplayAddNewLifeChangeButton(lifeChangeItems: LifeChangeItem[]): boolean {
    for (const item of lifeChangeItems) {
      if (!item.title) {
        return false;
      }
    }

    return true;
  }

  private updateBackEndWithUpdatedLifeChanges(updatedBlock: BlockLifeChanges): void {
    const programDataCodesToSave: ProgramDataCode[] = [
      {
        code: updatedBlock.program_data_code,
        value: updatedBlock.data.value,
      },
    ];

    this.courseContentService.saveProgramDataCodesAndUpdateStep(
      ApiUrlProgramData.loggedIn,
      programDataCodesToSave,
      updatedBlock,
    );
  }
}
